import * as React from 'react';
import Avatar from 'antd/lib/avatar';
import Select from 'antd/lib/select';
import Button from 'antd/lib/button';
import Collapse from 'antd/lib/collapse';
import Modal from 'antd/lib/modal';
import Input from 'antd/lib/input';
import Tag from 'antd/lib/tag';
import Tooltip from 'antd/lib/tooltip';
import Text from 'antd/lib/typography/Text';
import { useDispatch, useSelector } from 'react-redux';
import {
    CloseCircleOutlined,
    DownOutlined,
    EditOutlined,
    InfoCircleOutlined,
    LoadingOutlined,
    TagOutlined,
    ToolOutlined,
} from '@ant-design/icons';
import { IssueEntity, IssueState } from '@/types';
import { RootState } from '@/reducers/reducers';
import { antGrey, getAvatarColorForName, runwayColors, rwColors } from '@/components/styles/globalStyles';
import { selectReleasesForAppId, selectUserById } from '@/selectors/selectors';
import { selectAppOrgUsers, selectSortedGroups } from '@/selectors/memoizedSelectors';
import { displayTextForUserChecklistGroup, displayUserNameLong } from '@/helpers/displayHelpers';
import {
    TRY_TRANSITION_ISSUE,
    TRY_UPDATE_ISSUE,
    TRY_UPDATE_ISSUE_ASSIGNEES,
    TriageViewType,
    tryTransitionIssue,
    tryUpdateIssue,
    tryUpdateIssueAssignees,
} from '@/actions/triage';
import { useIsLoading, useUpdated } from '@/utils/hooks/helpers';
import PermissionedActionButton from '@/components/HOCs/PermissionedActionButton';
import { LABEL_STYLE, stateMeta } from './TriageIssueCard';
import { RequestFixModal } from './RequestFixModal';

const ASSIGNEE_USER_PREFIX = 'user:';
const ASSIGNEE_GROUP_PREFIX = 'group:';

const optionAvatar = (name: string, src?: string) => (
    <Avatar
        size={20}
        src={src}
        style={{ backgroundColor: getAvatarColorForName(name), fontSize: 11, border: 'none', flexShrink: 0 }}
    >
        {name.charAt(0).toUpperCase()}
    </Avatar>
);

const VALID_ISSUE_TRANSITIONS: Record<IssueState, IssueState[]> = {
    [IssueState.New]: [IssueState.Assigned, IssueState.FixRequested, IssueState.NotAPriority],
    [IssueState.Assigned]: [IssueState.New, IssueState.FixRequested, IssueState.NotAPriority],
    [IssueState.FixRequested]: [IssueState.FixApproved, IssueState.FixRejected, IssueState.NotAPriority],
    [IssueState.FixApproved]: [IssueState.FixMerged, IssueState.NotAPriority],
    [IssueState.Reopened]: [IssueState.FixRequested, IssueState.NotAPriority],
    [IssueState.FixMerged]: [],
    [IssueState.FixRejected]: [],
    [IssueState.NotAPriority]: [IssueState.New],
};

type EditField = 'title' | 'description';

const EDIT_FIELD_VALUE: Record<EditField, (issue: IssueEntity) => string> = {
    title: (issue) => issue.title ?? '',
    description: (issue) => issue.description ?? '',
};

const EDIT_FIELD_LABEL: Record<EditField, string> = {
    title: 'title',
    description: 'description',
};

const FieldColumn: React.FC<{ label: React.ReactNode; children: React.ReactNode; flex?: number }> = ({
    label,
    children,
    flex = 1,
}) => (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 6, flex, minWidth: 0 }}>
        <Text style={{ color: antGrey.grey8, fontSize: 14 }}>{label}</Text>
        {children}
    </div>
);

interface IssueSectionProps {
    orgId: string;
    issue: IssueEntity;
    viewType: TriageViewType;
}

export const IssueSection: React.FC<IssueSectionProps> = ({ orgId, issue, viewType }) => {
    const dispatch = useDispatch();
    const releases = useSelector((state: RootState) => selectReleasesForAppId(state, issue.appId));
    const appUsers = useSelector((state: RootState) => selectAppOrgUsers(state, issue.appId));
    const groups = useSelector((state: RootState) => selectSortedGroups(state, orgId));
    const reporter = useSelector((state: RootState) =>
        issue.reporter ? selectUserById(state, orgId, issue.appId, issue.reporter) : undefined,
    );

    const isAssigning = useIsLoading(TRY_UPDATE_ISSUE_ASSIGNEES, issue.id);
    const isTransitioning = useIsLoading(TRY_TRANSITION_ISSUE, issue.id);
    const isUpdatingIssue = useIsLoading(TRY_UPDATE_ISSUE, issue.id);
    const issueUpdated = useUpdated(TRY_UPDATE_ISSUE, issue.id);

    const [requestFixOpen, setRequestFixOpen] = React.useState(false);
    const [editOpen, setEditOpen] = React.useState(false);
    const [editField, setEditField] = React.useState<EditField>('title');
    const [editValue, setEditValue] = React.useState('');
    const [editSubmitted, setEditSubmitted] = React.useState(false);
    const openEdit = (field: EditField) => {
        setEditField(field);
        setEditValue(EDIT_FIELD_VALUE[field](issue));
        setEditSubmitted(false);
        setEditOpen(true);
    };
    const submitEdit = () => {
        setEditSubmitted(true);
        const payload = editField === 'title' ? { title: editValue.trim() } : { description: editValue.trim() };
        dispatch(tryUpdateIssue({ orgId, appId: issue.appId, issueId: issue.id, ...payload }));
    };
    React.useEffect(() => {
        if (editSubmitted && issueUpdated) {
            setEditSubmitted(false);
            setEditOpen(false);
        }
    }, [editSubmitted, issueUpdated]);

    const canRequestFix = !issue.fixRequest && VALID_ISSUE_TRANSITIONS[issue.state].includes(IssueState.FixRequested);
    const canNotPriority = VALID_ISSUE_TRANSITIONS[issue.state].includes(IssueState.NotAPriority);
    const assigneeId = issue.assigneeUserIds?.[0];
    const assigneeGroupId = issue.assigneeGroupIds?.[0];
    const version = issue.versions?.[0];
    const reporterName = reporter ? displayUserNameLong(reporter) : (issue.reporter ?? '');

    const statusOptions = [issue.state, ...VALID_ISSUE_TRANSITIONS[issue.state]].map((state) => ({
        value: state,
        label: (
            <span style={{ display: 'inline-flex', alignItems: 'center', gap: 8, minWidth: 0 }}>
                <span style={{ display: 'inline-flex', flexShrink: 0 }}>{stateMeta(state).icon}</span>
                <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    {stateMeta(state).label}
                </span>
            </span>
        ),
    }));

    const versionOptions = releases.map((release) => ({
        value: release.version,
        label: release.displayableVersion || release.version,
    }));

    const assigneeOptions = [
        ...appUsers.map((user) => {
            const name = displayUserNameLong(user);
            return {
                value: `${ASSIGNEE_USER_PREFIX}${user.id}`,
                name,
                avatarSrc: user.commsAvatars?.image32,
                label: (
                    <span
                        style={{ display: 'inline-flex', alignItems: 'center', gap: 8, minWidth: 0, maxWidth: '100%' }}
                    >
                        {optionAvatar(name, user.commsAvatars?.image32)}
                        <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                            {name}
                        </span>
                    </span>
                ),
            };
        }),
        ...groups.map((group) => {
            const name = displayTextForUserChecklistGroup(group);
            return { value: `${ASSIGNEE_GROUP_PREFIX}${group.id}`, name, avatarSrc: undefined, label: name };
        }),
    ];
    const assigneeValue =
        assigneeId ? `${ASSIGNEE_USER_PREFIX}${assigneeId}`
        : assigneeGroupId ? `${ASSIGNEE_GROUP_PREFIX}${assigneeGroupId}`
        : undefined;

    const onAssigneeChange = (values: string[]) => {
        const value = values[values.length - 1];
        const isGroup = !!value && value.startsWith(ASSIGNEE_GROUP_PREFIX);
        dispatch(
            tryUpdateIssueAssignees({
                orgId,
                appId: issue.appId,
                issueId: issue.id,
                assigneeUserIds: value && !isGroup ? [value.slice(ASSIGNEE_USER_PREFIX.length)] : [],
                assigneeGroupIds: isGroup ? [value.slice(ASSIGNEE_GROUP_PREFIX.length)] : [],
                viewType,
            }),
        );
    };

    const versionSelect = (
        <Select
            value={version}
            options={versionOptions}
            disabled={isUpdatingIssue}
            suffixIcon={isUpdatingIssue ? <LoadingOutlined spin style={{ color: antGrey.grey6 }} /> : undefined}
            placeholder="--"
            style={{ width: '100%' }}
            onChange={(value) =>
                dispatch(tryUpdateIssue({ orgId, appId: issue.appId, issueId: issue.id, version: value }))
            }
        />
    );

    return (
        <>
            <Collapse
                className="triage-collapse"
                ghost
                bordered={false}
                defaultActiveKey={['section']}
                items={[
                    {
                        key: 'section',
                        showArrow: false,
                        style: { borderTop: issue.fixRequest ? `1px solid ${antGrey.grey4}` : undefined },
                        label: (
                            <div style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
                                <TagOutlined style={{ color: antGrey.grey7 }} />
                                <Text style={{ color: antGrey.grey8, fontSize: 14 }}>Issue</Text>
                            </div>
                        ),
                        extra: (
                            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                                {stateMeta(issue.state).icon}
                                <Text style={LABEL_STYLE}>{stateMeta(issue.state).label}</Text>
                            </div>
                        ),
                        children: (
                            <>
                                <div style={{ padding: 16, display: 'flex', flexDirection: 'column', gap: 16 }}>
                                    <div
                                        style={{
                                            display: 'flex',
                                            alignItems: 'flex-start',
                                            justifyContent: 'space-between',
                                        }}
                                    >
                                        <Text
                                            style={{
                                                fontWeight: 600,
                                                fontSize: 20,
                                                color: issue?.title ? antGrey.grey8 : antGrey.grey6,
                                                fontStyle: issue?.title ? undefined : 'italic',
                                            }}
                                        >
                                            {issue?.title || 'Add a title'}
                                        </Text>
                                        <PermissionedActionButton
                                            type="link"
                                            size="small"
                                            icon={<EditOutlined />}
                                            onClick={() => openEdit('title')}
                                        />
                                    </div>
                                    <div
                                        style={{
                                            display: 'flex',
                                            alignItems: 'flex-start',
                                            justifyContent: 'space-between',
                                        }}
                                    >
                                        <Text
                                            style={{
                                                fontSize: 16,
                                                color: issue?.description ? antGrey.grey8 : antGrey.grey6,
                                                fontStyle: issue?.description ? undefined : 'italic',
                                            }}
                                        >
                                            {issue?.description || 'Add a description'}
                                        </Text>
                                        <PermissionedActionButton
                                            type="link"
                                            size="small"
                                            icon={<EditOutlined />}
                                            onClick={() => openEdit('description')}
                                        />
                                    </div>
                                </div>
                                <div style={{ display: 'flex', gap: 12, padding: '0 16px 16px' }}>
                                    <FieldColumn label="Status" flex={1.4}>
                                        <Select
                                            value={issue.state}
                                            options={statusOptions}
                                            style={{ width: '100%' }}
                                            suffixIcon={<DownOutlined style={{ color: antGrey.grey9 }} />}
                                            onChange={(value) => {
                                                if (value === issue.state) {
                                                    return;
                                                }
                                                if (value === IssueState.FixRequested) {
                                                    setRequestFixOpen(true);
                                                    return;
                                                }
                                                dispatch(
                                                    tryTransitionIssue({
                                                        orgId,
                                                        appId: issue.appId,
                                                        issueId: issue.id,
                                                        targetState: value as IssueState,
                                                        viewType,
                                                    }),
                                                );
                                            }}
                                        />
                                    </FieldColumn>
                                    <FieldColumn label="Reported version" flex={0.8}>
                                        {versionSelect}
                                    </FieldColumn>
                                    <FieldColumn label="Reporter">
                                        <div
                                            style={{
                                                display: 'flex',
                                                alignItems: 'center',
                                                minHeight: 32,
                                                padding: '3px 4px',
                                                border: `1px solid ${antGrey.grey5}`,
                                                borderRadius: 6,
                                                background: runwayColors.white,
                                            }}
                                        >
                                            {reporterName && (
                                                <span
                                                    style={{
                                                        display: 'inline-flex',
                                                        alignItems: 'center',
                                                        gap: 6,
                                                        maxWidth: '100%',
                                                        padding: '1px 8px',
                                                        borderRadius: 4,
                                                        background: antGrey.grey3,
                                                    }}
                                                >
                                                    {optionAvatar(reporterName, reporter?.commsAvatars?.image32)}
                                                    <Text ellipsis style={{ color: antGrey.grey8, fontSize: 13 }}>
                                                        {reporterName}
                                                    </Text>
                                                </span>
                                            )}
                                        </div>
                                    </FieldColumn>
                                    <FieldColumn
                                        label={
                                            <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
                                                Assignee
                                                {issue.isAutoAssigned && (
                                                    <Tooltip title="Auto-assigned by Runway" placement="top">
                                                        <InfoCircleOutlined
                                                            style={{ color: antGrey.grey6, fontSize: 12 }}
                                                        />
                                                    </Tooltip>
                                                )}
                                            </span>
                                        }
                                    >
                                        <Select
                                            mode="multiple"
                                            showSearch
                                            className="triage-assignee-select"
                                            disabled={isAssigning}
                                            suffixIcon={
                                                isAssigning ?
                                                    <LoadingOutlined spin style={{ color: antGrey.grey6 }} />
                                                :   null
                                            }
                                            value={assigneeValue ? [assigneeValue] : []}
                                            options={assigneeOptions}
                                            filterOption={(input, option) =>
                                                (option?.name ?? '').toLowerCase().includes(input.toLowerCase())
                                            }
                                            tagRender={({ value, closable, onClose }) => {
                                                const option = assigneeOptions.find((o) => o.value === value);
                                                const name = option?.name ?? String(value);
                                                return (
                                                    <Tag
                                                        closable={closable}
                                                        onClose={onClose}
                                                        style={{
                                                            display: 'inline-flex',
                                                            alignItems: 'center',
                                                            gap: 6,
                                                            height: 24,
                                                            margin: 0,
                                                            ...(issue.isAutoAssigned ?
                                                                {
                                                                    background: runwayColors.white,
                                                                    border: `1px dashed ${antGrey.grey5}`,
                                                                }
                                                            :   {}),
                                                        }}
                                                    >
                                                        {optionAvatar(name, option?.avatarSrc)}
                                                        <span
                                                            style={{
                                                                display: 'inline-block',
                                                                maxWidth: 75,
                                                                overflow: 'hidden',
                                                                textOverflow: 'ellipsis',
                                                                whiteSpace: 'nowrap',
                                                                verticalAlign: 'middle',
                                                            }}
                                                        >
                                                            {name}
                                                        </span>
                                                    </Tag>
                                                );
                                            }}
                                            placeholder="--"
                                            style={{ width: '100%' }}
                                            onChange={onAssigneeChange}
                                        />
                                    </FieldColumn>
                                </div>
                                {(canRequestFix || canNotPriority) && (
                                    <div style={{ display: 'flex', gap: 12, padding: '0 16px 16px' }}>
                                        {canRequestFix && (
                                            <Button
                                                type="primary"
                                                icon={<ToolOutlined />}
                                                onClick={() => setRequestFixOpen(true)}
                                                style={{
                                                    flex: 1,
                                                    height: 40,
                                                    backgroundColor: rwColors.statusYellow,
                                                    borderColor: rwColors.statusYellow,
                                                    fontWeight: 600,
                                                }}
                                            >
                                                Request approval for a fix
                                            </Button>
                                        )}
                                        {canNotPriority && (
                                            <Button
                                                icon={<CloseCircleOutlined />}
                                                loading={isTransitioning}
                                                onClick={() =>
                                                    dispatch(
                                                        tryTransitionIssue({
                                                            orgId,
                                                            appId: issue.appId,
                                                            issueId: issue.id,
                                                            targetState: IssueState.NotAPriority,
                                                            viewType,
                                                        }),
                                                    )
                                                }
                                                style={{
                                                    flex: 1,
                                                    height: 40,
                                                    backgroundColor: antGrey.grey7,
                                                    borderColor: antGrey.grey7,
                                                    color: runwayColors.white,
                                                    fontWeight: 600,
                                                }}
                                            >
                                                Not a priority
                                            </Button>
                                        )}
                                    </div>
                                )}
                            </>
                        ),
                    },
                ]}
            />
            <RequestFixModal
                open={requestFixOpen}
                onClose={() => setRequestFixOpen(false)}
                orgId={orgId}
                appId={issue.appId}
                issue={issue}
                viewType={viewType}
            />
            <Modal
                title={`Edit ${EDIT_FIELD_LABEL[editField]}`}
                open={editOpen}
                onCancel={() => setEditOpen(false)}
                onOk={submitEdit}
                okText="Save"
                okButtonProps={{ disabled: !editValue.trim() }}
                confirmLoading={isUpdatingIssue}
                destroyOnClose
                width={560}
            >
                {editField === 'title' ?
                    <Input value={editValue} onChange={(e) => setEditValue(e.target.value)} autoFocus />
                :   <Input.TextArea
                        value={editValue}
                        onChange={(e) => setEditValue(e.target.value)}
                        autoSize={{ minRows: 3, maxRows: 8 }}
                        autoFocus
                    />
                }
            </Modal>
        </>
    );
};
